export const WIDGET_SUB_TABS = [
  { key: "appearance", label: "Appearance" },
  { key: "behaviour", label: "Behaviour" },
  { key: "install", label: "Install" },
];

export const PREVIEW_PLATFORMS = ["Web", "Android", "ios"];

export const MAX_PRE_CHAT_FIELDS = 4;

export const DEFAULT_PRE_CHAT_FIELDS = [
  { id: "name", label: "Name", enabled: true, required: true, locked: true },
  { id: "email", label: "Email", enabled: true, required: true, locked: true },
];

export const DEFAULT_WIDGET_SETTINGS = {
  primaryColor: "#4F46E5",
  position: "right",
  greetingText: "Hi there! 👋",
  greetingSubtitle: "Please Fill in the form below before start the chat",
  welcomeMessage: "How can we help you today?",
  logo: "",
  showWhenOffline: true,
  preChatForm: true,
  startChatAlways: true,
  allowGuest: true,
  preChatFields: DEFAULT_PRE_CHAT_FIELDS.map((field) => ({ ...field })),
};

export const BEHAVIOUR_OPTION_KEYS = ["showWhenOffline", "preChatForm"];

function clonePreChatFields(fields) {
  return (fields || []).map((field) => ({ ...field }));
}

export function createDefaultPreChatFields() {
  return clonePreChatFields(DEFAULT_PRE_CHAT_FIELDS);
}

/** Labels cannot be blank/whitespace or just "." */
export function getPreChatFieldLabelError(label) {
  const trimmed = String(label ?? "").trim();
  if (!trimmed) {
    return "Label is required";
  }
  if (trimmed === ".") {
    return "Enter a valid label";
  }
  return "";
}

export function isValidPreChatFieldLabel(label) {
  return !getPreChatFieldLabelError(label);
}

/** Required appearance text fields cannot be blank/whitespace. */
export function getRequiredTextError(value, fieldLabel = "This field") {
  if (!String(value ?? "").trim()) {
    return `${fieldLabel} is required`;
  }
  return "";
}

export function getAppearanceTextErrors(settings = {}) {
  return {
    greetingText: getRequiredTextError(settings.greetingText, "Greeting text"),
    greetingSubtitle: getRequiredTextError(settings.greetingSubtitle, "Greeting subtitle"),
    welcomeMessage: getRequiredTextError(settings.welcomeMessage, "Welcome message"),
  };
}

export function hasAppearanceTextErrors(settings = {}) {
  const errors = getAppearanceTextErrors(settings);
  return Boolean(errors.greetingText || errors.greetingSubtitle || errors.welcomeMessage);
}

export function normalizePreChatFields(fields) {
  const source = Array.isArray(fields) && fields.length > 0 ? fields : DEFAULT_PRE_CHAT_FIELDS;
  const normalized = [];

  source.forEach((field, index) => {
    if (normalized.length >= MAX_PRE_CHAT_FIELDS) return;

    const id = String(field?.id || `field_${index + 1}`);
    const isName = id === "name";
    const isEmail = id === "email";
    const locked = isName || isEmail;
    const defaultLabel = isName ? "Name" : isEmail ? "Email" : `Field ${index + 1}`;
    // Keep empty string so users can clear and retype labels freely
    const label = typeof field?.label === "string" ? field.label : defaultLabel;

    normalized.push({
      id: isName ? "name" : isEmail ? "email" : id,
      label,
      enabled: field?.enabled !== false,
      required: field?.required !== false,
      locked,
    });
  });

  const hasName = normalized.some((field) => field.id === "name");
  const hasEmail = normalized.some((field) => field.id === "email");

  if (!hasName) {
    normalized.unshift({ ...DEFAULT_PRE_CHAT_FIELDS[0] });
  }
  if (!hasEmail) {
    const nameIndex = normalized.findIndex((field) => field.id === "name");
    normalized.splice(nameIndex + 1, 0, { ...DEFAULT_PRE_CHAT_FIELDS[1] });
  }

  return normalized.slice(0, MAX_PRE_CHAT_FIELDS).map((field) => ({ ...field }));
}

export function normalizeBehaviourSettings(settings) {
  const next = {
    ...settings,
    preChatFields: normalizePreChatFields(settings.preChatFields),
  };

  if (next.startChatAlways) {
    next.showWhenOffline = true;
  }

  return next;
}

export function buildBehaviourPatch(key, enabled) {
  if (key === "startChatAlways") {
    if (enabled) {
      return { startChatAlways: true, showWhenOffline: true };
    }
    return { startChatAlways: false };
  }

  if (key === "showWhenOffline") {
    return { showWhenOffline: enabled };
  }

  return { [key]: enabled };
}

/**
 * Preview / runtime decision tree for chat widget visibility and mode.
 * Does not use showWhenOffline to swap chat vs form content when agents are online.
 */
export function resolveWidgetPreviewState({
  agentsOnline = true,
  preChatForm = true,
  startChatAlways = true,
  showWhenOffline = false,
} = {}) {
  if (agentsOnline) {
    return {
      visible: true,
      mode: preChatForm ? "prechat" : "direct_chat",
    };
  }

  if (startChatAlways) {
    return {
      visible: true,
      mode: preChatForm ? "prechat" : "direct_chat",
    };
  }

  if (showWhenOffline) {
    return {
      visible: true,
      mode: "ticket",
    };
  }

  return {
    visible: false,
    mode: "hidden",
  };
}

export function buildInstallSnippet(businessId, groupId) {
  return `<script>
  (function () {
    var script = document.createElement("script");
    script.src = "https://trustchatwidget.netlify.app/chat-widget.js";
    script.onload = function () {
      ChatWidget({
        businessId: "${businessId || "YOUR_BUSINESS_ID"}",
        groupId: "${groupId || "YOUR_GROUP_ID_HERE"}",
      });
    };
    document.head.appendChild(script);
  })();
</script>`;
}

function normalizePosition(value) {
  return String(value || "right").toLowerCase();
}

function capitalizePosition(value) {
  const normalized = normalizePosition(value);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function buildPreChatFieldsFromApi(appearance, behaviour, data) {
  if (Array.isArray(behaviour?.pre_chat_fields) && behaviour.pre_chat_fields.length) {
    return normalizePreChatFields(behaviour.pre_chat_fields);
  }
  if (Array.isArray(data?.pre_chat_fields) && data.pre_chat_fields.length) {
    return normalizePreChatFields(data.pre_chat_fields);
  }

  const nameRequired = behaviour?.name_required ?? data?.name_required;
  const emailRequired = behaviour?.email_required ?? data?.email_required;

  return normalizePreChatFields([
    {
      id: "name",
      label: "Name",
      enabled: true,
      required: nameRequired !== false,
      locked: true,
    },
    {
      id: "email",
      label: "Email",
      enabled: true,
      required: emailRequired !== false,
      locked: true,
    },
  ]);
}

export function mapApiSettingsToForm(apiResponse) {
  if (!apiResponse) return null;

  const appearance = apiResponse.ui?.appearance;
  const behaviour = apiResponse.ui?.behaviour;
  const data = apiResponse.data;

  if (!appearance && !behaviour && !data) return null;

  const startChatAlways = Boolean(
    behaviour?.start_chat_always ??
      data?.start_chat_always ??
      DEFAULT_WIDGET_SETTINGS.startChatAlways,
  );

  return normalizeBehaviourSettings({
    primaryColor:
      appearance?.primary_color ??
      data?.theme_color ??
      DEFAULT_WIDGET_SETTINGS.primaryColor,
    position: normalizePosition(appearance?.position ?? data?.alignment),
    greetingText:
      appearance?.greeting_text ??
      data?.welcome_text ??
      DEFAULT_WIDGET_SETTINGS.greetingText,
    greetingSubtitle:
      appearance?.greeting_subtitle ??
      data?.greeting_subtitle ??
      DEFAULT_WIDGET_SETTINGS.greetingSubtitle,
    welcomeMessage:
      appearance?.welcome_message ??
      data?.welcome_message ??
      DEFAULT_WIDGET_SETTINGS.welcomeMessage,
    logo: appearance?.logo ?? data?.logo ?? DEFAULT_WIDGET_SETTINGS.logo,
    showWhenOffline: startChatAlways
      ? true
      : Boolean(
          behaviour?.show_widget_when_offline ??
            data?.show_widget_when_offline ??
            DEFAULT_WIDGET_SETTINGS.showWhenOffline,
        ),
    preChatForm:
      behaviour?.pre_chat_form ?? data?.pre_chat_form ?? DEFAULT_WIDGET_SETTINGS.preChatForm,
    startChatAlways,
    allowGuest: Boolean(
      behaviour?.allow_guest ?? data?.allow_guest ?? DEFAULT_WIDGET_SETTINGS.allowGuest,
    ),
    preChatFields: buildPreChatFieldsFromApi(appearance, behaviour, data),
  });
}

export function mapFormToApiPayload(form, { includePendingLogo = false } = {}) {
  const hasLogo = Boolean(form.logo) || includePendingLogo;
  const preChatFields = normalizePreChatFields(form.preChatFields);
  const nameField = preChatFields.find((field) => field.id === "name");
  const emailField = preChatFields.find((field) => field.id === "email");
  const startChatAlways = Boolean(form.startChatAlways);
  const showWhenOffline = startChatAlways ? true : Boolean(form.showWhenOffline);

  return {
    theme_color: form.primaryColor,
    alignment: capitalizePosition(form.position),
    welcome_text: form.greetingText,
    greeting_subtitle: form.greetingSubtitle,
    welcome_message: form.welcomeMessage,
    show_widget_when_offline: showWhenOffline,
    pre_chat_form: form.preChatForm,
    start_chat_always: startChatAlways,
    allow_guest: form.allowGuest,
    show_logo: hasLogo,
    name_required: Boolean(nameField?.enabled && nameField?.required),
    email_required: Boolean(emailField?.enabled && emailField?.required),
    pre_chat_fields: JSON.stringify(preChatFields),
  };
}

export function buildWidgetFormData(form, logoFile = null) {
  const api = mapFormToApiPayload(form, { includePendingLogo: Boolean(logoFile) });
  const formData = new FormData();

  Object.entries(api).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    formData.append(key, typeof value === "boolean" ? String(value) : value);
  });

  if (logoFile) {
    formData.append("logo", logoFile);
  }

  return formData;
}

export function applySaveResponseToForm(currentSettings, apiResponse) {
  const mapped = mapApiSettingsToForm(apiResponse);
  if (!mapped) return currentSettings;

  const appearance = apiResponse?.ui?.appearance;
  const behaviour = apiResponse?.ui?.behaviour;
  const data = apiResponse?.data;
  const hasGreetingSubtitle =
    appearance?.greeting_subtitle != null || data?.greeting_subtitle != null;
  const hasPreChatFields =
    Array.isArray(behaviour?.pre_chat_fields) ||
    Array.isArray(data?.pre_chat_fields) ||
    typeof data?.pre_chat_fields === "string";

  return {
    ...mapped,
    // Preserve until backend returns these fields
    greetingSubtitle: hasGreetingSubtitle
      ? mapped.greetingSubtitle
      : currentSettings.greetingSubtitle,
    preChatFields: hasPreChatFields
      ? mapped.preChatFields
      : normalizePreChatFields(currentSettings.preChatFields),
    logo: mapped.logo || "",
  };
}

export function formatGroupLabel(group) {
  if (!group) return "";
  const email =
    group.raw?.group_email ||
    group.raw?.email ||
    group.raw?.support_email ||
    group.raw?.channels?.[0]?.channel_id ||
    "";
  return email ? `${group.name} - ${email}` : group.name;
}

export {
  getActiveGroups,
  getDefaultGroupId,
  getDefaultGroupId as getDefaultChatWidgetGroupId,
  isGroupArchived,
} from "../../shared/groupUtils";
